'use client';

import { AppError } from '@/utility/errorClasses';
import { parseResponse } from './parseResponse';

const withClientLocationHeaders = (headers?: HeadersInit): Headers => {
  const merged = new Headers(headers);
  if (typeof window !== 'undefined') {
    if (!merged.has('x-pathname')) {
      merged.set('x-pathname', window.location.pathname);
    }
    if (!merged.has('x-search')) {
      merged.set('x-search', window.location.search);
    }
  }
  return merged;
};

export const get = async <T>(url: string, config?: RequestInit): Promise<T> => {
  const params = config || {};
  params.headers = withClientLocationHeaders(params.headers);
  return fetch(url, params).then((response) => {
    if (!response.ok) {
      throw new AppError(`GET ${url} failed with status ${response.status}`, response.status);
    }
    return response.json() as Promise<T>;
  });
};

export const post = async <T>(url: string, body: unknown, config?: RequestInit): Promise<T> => {
  const headers = withClientLocationHeaders(config?.headers);
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const params = {
    method: 'POST',
    body: JSON.stringify(body),
    headers,
    ...config,
  };

  return fetch(url, params).then((response) => {
    if (!response.ok) {
      return parseResponse(response).then((responseBody) => {
        throw new AppError(
          `POST ${url} failed with status ${response.status}`,
          response.status,
          responseBody
        );
      });
    }
    return response.json() as Promise<T>;
  });
};

export const put = async <T>(url: string, body: unknown, config?: RequestInit): Promise<T> => {
  const headers = withClientLocationHeaders(config?.headers);
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  return fetch(url, {
    method: 'PUT',
    body: JSON.stringify(body),
    headers,
    ...config,
  }).then((response) => {
    if (!response.ok) {
      return parseResponse(response).then((responseBody) => {
        throw new AppError(
          `PUT ${url} failed with status ${response.status}`,
          response.status,
          responseBody
        );
      });
    }
    return response.json() as Promise<T>;
  });
};

export const patch = async <T>(url: string, body: unknown, config?: RequestInit): Promise<T> => {
  const headers = withClientLocationHeaders(config?.headers);
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  return fetch(url, {
    method: 'PATCH',
    body: JSON.stringify(body),
    headers,
    ...config,
  }).then(async (response) => {
    if (!response.ok) {
      return parseResponse(response).then((responseBody) => {
        throw new AppError(
          `Patch ${url} failed with status ${response.status}`,
          response.status,
          responseBody
        );
      });
    }
    return response.json() as Promise<T>;
  });
};

export const toQueryString = (params: Record<string, unknown>): string => {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      searchParams.set(key, value.join(','));
    } else {
      searchParams.set(key, String(value));
    }
  }
  return searchParams.toString();
};

export const remove = async <T>(url: string, config?: RequestInit): Promise<T> => {
  const headers = withClientLocationHeaders(config?.headers);
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  return fetch(url, {
    method: 'DELETE',
    headers,
    ...config,
  }).then((response) => {
    if (!response.ok) {
      return parseResponse(response).then(() => {
        throw new AppError(`DELETE ${url} failed with status ${response.status}`, response.status);
      });
    }
    return response.json() as Promise<T>;
  });
};
